/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import Title from 'antd/lib/typography/Title';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
  Row, Button, Col, Form, Popover, Typography, Icon, Input, Tooltip,
} from 'antd';
import * as PropTypes from 'prop-types';
import { inspectionShape } from '../utility/inspection-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { requestSendInspectionReport } from '../../../../state/ui/inspection/inspectionUiActions';
import { selectSendReportRequest } from '../../../../state/ui/inspection/inspectionUiReducer';
import {getDefaultLocale} from "../../../../configWeb";
import {getLocalTimeDisplay} from "../../bynx/packageUtil";

const { Text } = Typography;


const ResendInspectionPopover = Form.create({ name: 'send_inspection' })(({
  form, currentAddress, onSend, emailSending, ...rest
}) => {
  // React State
  const intl = useIntl();
  const [visible, setVisible] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      onSend(values.email);
      setVisible(false);
    } catch (_) {
      // Validations will appear on screen.
    }
  };

  return (
    <Popover
      {...rest}
      visible={visible}
      trigger="click"
      placement="leftTop"
      style={{ width: '300px' }}
      title={intl.formatMessage({ id: 'enterEmailAddress' })}
      content={(
        <Form onSubmit={e => onSubmit(e)}>
          <Row span={24} style={{ width: '250px' }}>
            <Text><FormattedMessage id="sendReportDesc" /></Text>
          </Row>
          <Row span={24}>
            <Form.Item
              className="my-details-form-item"
              style={{ marginBottom: '0', marginRight: '0.0em' }}
            >
              {form.getFieldDecorator('email', {
                initialValue: currentAddress,
                rules: [
                  { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
                  { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
                ],
              })(
                <Input
                  className="my-details-form-item"
                  prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,0.25)' }} />}
                  type="email"
                  placeholder={intl.formatMessage({ id: 'email' })}
                  maxLength={80}
                  onPressEnter={onSubmit}
                />,
              )}
            </Form.Item>
          </Row>
          <Row span={24} type="flex" justify="end">
            <Form.Item style={{ marginBottom: '0' }}>
              <Button style={{ marginRight: '0.6em' }} onClick={() => { setVisible(false); }}>{intl.formatMessage({ id: 'cancel' })}</Button>
              <Button type="primary" htmlType="submit">{intl.formatMessage({ id: 'sendEmail' })}</Button>
            </Form.Item>
          </Row>
        </Form>
      )}
    >
      <Button type="primary" style={{ marginLeft: '0.6em' }} icon="mail" loading={emailSending} onClick={() => setVisible(!visible)}>
        <FormattedMessage id="sendReport" />
      </Button>
    </Popover>
  );
});

ResendInspectionPopover.propTypes = {
  currentAddress: PropTypes.string,
  onSend: PropTypes.func.isRequired,
};

/**
 * A value that appears immediately below the header.
 * This contains meta information about the inspection.
 * @param {string} props.label The describing label
 * @param {string} props.value The value that matches the label.
 */
function SubTitleValue({ label, value, tooltip }) {
  return (
    <span className="title-desc">
      <div className="title-desc-label"><FormattedMessage id={label} /></div>
      <div className="title-desc-value">
        {tooltip
          ? <Tooltip title={tooltip}>{value || <FormattedMessage id="noData" />}</Tooltip>
          : value || <FormattedMessage id="noData" />}
      </div>
    </span>
  );
}

SubTitleValue.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltip: PropTypes.string,
};

SubTitleValue.defaultProps = {
  value: null,
  tooltip: null,
};

/**
 * A value that appears immediately below the header.
 * This contains meta information about the inspection.
 * @param {string} props.label The describing label
 * @param {string} props.value The value that matches the label.
 */
function SubTitleLocation({ label, latitude, longitude }) {
  const hasLocation = latitude != null & longitude != null; //one & as must check both

  return (
    <span className="title-desc">
      <div className="title-desc-label"><FormattedMessage id={label} /></div>
      <div className="title-desc-value" title={hasLocation ? latitude.toFixed(2)+ ", "+ longitude.toFixed(2) : null}>
        {
          hasLocation
          ? (
              <a href={`https://www.google.com/maps?q=${latitude},${longitude}`} rel="noreferrer noopener" target="_blank">
                <FormattedMessage id="view" />
              </a>
            )
            : <FormattedMessage id="noData" />
        }
      </div>
    </span>
  );
}

SubTitleLocation.propTypes = {
  label: PropTypes.string.isRequired,
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SubTitleLocation.defaultProps = {
  latitude: null,
  longitude: null,
};


/**
 * Displays the leading information about the Inspection.
 * This is mostly meta data that's not categorised else where.
 * If being viewed in the back office, the user can re-send the inspection email.
 * @param {object} props.inspection The inspection being presented.
 */
export default function InspectionHeader({ inspection }) {
  // Redux State
  const resendRequest = useSelector(selectSendReportRequest);
  const emailSending = !!(resendRequest && resendRequest.isLoading);
  const sessionUser = useSelector(selectSessionUser);

  const intl = useIntl();

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  // Local time display - submitted date
  const showLocalTime = inspection.submittedLocalTime ? getLocalTimeDisplay(inspection.submittedLocalTime, userLocale) : null;

  // Redux Actions
  const dispatch = useDispatch();
  const onSend = toAddress => dispatch(requestSendInspectionReport(inspection.inspectionCode, toAddress));

  return (
    <Row className="via-centre">
      <Row type="flex" justify="end">
        <Col style={{ width: '150px' }}>
          {sessionUser && sessionUser.linkedRoles.length !== 0 && (
            <ResendInspectionPopover
              onSend={onSend}
              currentAddress={inspection.customerEmail}
              emailSending={emailSending}
            />
          )}
        </Col>
      </Row>
      <Row type="flex" justify="space-around">
        <Col style={{ width: '500px' }}>
          <Title level={2} className="inspection-title">
            <FormattedMessage id="inspectionHeaderRegNo" values={{ regNo: <span style={{ whiteSpace: 'nowrap' }}>{inspection.vehicle.regNumber}</span> }} />
          </Title>
        </Col>
      </Row>
      <Row>
        <div className="title-descs">
          <SubTitleValue label="inspectionReference" value={inspection.inspectionCode} />
          <SubTitleLocation label="inspectionLocation" latitude={inspection.coordinates && inspection.coordinates.latitude} longitude={inspection.coordinates && inspection.coordinates.longitude} />
          <SubTitleValue label="yourReference" value={inspection.externalContextReference} />
          <SubTitleValue label="localTime" tooltip={inspection.localTime ? `${intl.formatMessage({ id: 'created' })} ${getLocalTimeDisplay(inspection.localTime, userLocale)}` : null} value={showLocalTime} />
        </div>
      </Row>
    </Row>
  );
}

InspectionHeader.propTypes = {
  inspection: inspectionShape.isRequired,
};
